/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useSyncExternalStore } from 'react';

/** Forced data lifecycle requested by the Dev Cockpit, or `'off'` for real flags. */
export type DataStateOverride = 'off' | 'loading' | 'error' | 'empty';

/** Surface data-flags an override is applied on top of. */
export interface DataStateFlags {
  /** True while the first fetch has not resolved. */
  isLoading: boolean;
  /** True when the resolved fetch failed. */
  isError?: boolean;
  /** True when the resolved data set is empty. */
  isEmpty: boolean;
}

type DataStateSimulatorModule = typeof import('@features/dev-cockpit/services/state-simulator');

const simulatorModules = import.meta.glob<DataStateSimulatorModule>(
  '@features/dev-cockpit/services/state-simulator.ts',
  { eager: true },
);

/**
 * Resolves the dev-cockpit state-simulator module, but only when the cockpit
 * feature flag is enabled.
 *
 * @returns The simulator module, or `null` when the cockpit is off/absent.
 */
function resolveSimulatorModule(): DataStateSimulatorModule | null {
  if (import.meta.env.FF_DEV_COCKPIT !== 'true') return null;
  return Object.values(simulatorModules)[0] ?? null;
}

let dataStateSimulator: DataStateSimulatorModule | null = resolveSimulatorModule();

/**
 * No-op unsubscribe used when the dev-cockpit module is absent.
 *
 * @returns Nothing; there is no subscription to tear down.
 */
function noopUnsubscribe(): void {
  /* nothing to clean up when the cockpit is off */
}

/**
 * Subscribes to the dev-cockpit store when present, so a forced lifecycle change
 * re-renders the surface. Returns a no-op when the cockpit is off.
 *
 * @param onChange React's store-change callback.
 * @returns The unsubscribe function.
 */
function subscribeToOverride(onChange: () => void): () => void {
  const mod = dataStateSimulator;
  if (!mod) return noopUnsubscribe;
  return mod.useStateSimulatorStore.subscribe(onChange);
}

/**
 * Reads the current forced lifecycle from the dev-cockpit store, or `'off'` when
 * the cockpit module is absent.
 *
 * @returns The forced lifecycle snapshot.
 */
function getOverrideSnapshot(): DataStateOverride {
  const mod = dataStateSimulator;
  if (!mod) return 'off';
  return mod.useStateSimulatorStore.getState().simulateDataState as DataStateOverride;
}

/**
 * Test-only injector to set (or clear with `null`) the dev-cockpit simulator
 * module, mirroring the lazy-glob decoupling used in `request.ts`.
 *
 * @param mod The simulator module to use, or `null` to disable the override.
 */
export function __setDataStateOverrideForTest(mod: DataStateSimulatorModule | null): void {
  dataStateSimulator = mod;
}

/**
 * Reads the Dev Cockpit "Simulate Surface State" toggle through the lazy-glob
 * seam so production builds tree-shake the dev-cockpit chunk away. Always calls
 * exactly one hook (`useSyncExternalStore`), so the hook-call count is constant
 * across every render even if the cockpit module appears or disappears
 * mid-session (no Rules-of-Hooks violation).
 *
 * @returns The forced lifecycle, or `'off'` when the cockpit is disabled.
 */
export function useDataStateOverride(): DataStateOverride {
  return useSyncExternalStore(subscribeToOverride, getOverrideSnapshot, getOverrideSnapshot);
}

/**
 * ORs a Dev Cockpit override into a surface's data-flags so early-return pages
 * (whose `DataState` unmounts once loaded) still honor the forced lifecycle.
 *
 * @param override The forced lifecycle from {@link useDataStateOverride}.
 * @param flags The surface's real `{ isLoading, isError?, isEmpty }` flags.
 * @returns The effective flags with the override ORed in.
 */
export function applyDataStateOverride(
  override: DataStateOverride,
  flags: DataStateFlags,
): Required<DataStateFlags> {
  return {
    isLoading: flags.isLoading || override === 'loading',
    isError: Boolean(flags.isError) || override === 'error',
    isEmpty: flags.isEmpty || override === 'empty',
  };
}
